/**
 * Single source of truth for all alumni profile option lists,
 * label maps, and display↔db value converters.
 *
 * Used by: AlumniProfilePage, ManageAlumni, ManageSurveys,
 *          JobMatchingPage, EmployerJobManagement, AlumniSignupPage
 */

import type { EmploymentType, ExperienceLevel } from '../types'

/* ──────────────── Employment Status ──────────────────────── */

export const EMPLOYMENT_STATUS_OPTIONS = [
  'Employed', 'Unemployed', 'Self-Employed', 'Studying', 'Studying (Also Employed)',
] as const

/** Statuses where job-related fields are relevant */
export const EMPLOYED_STATUSES = new Set(['Employed', 'Self-Employed', 'Studying (Also Employed)'])

/** Check if employment-related fields should be shown/enabled */
export function isEmployed(status: string | undefined | null): boolean {
  if (!status) return false
  return EMPLOYED_STATUSES.has(status)
}

/* ──────────────── Employer Type ──────────────────────────── */

export const EMPLOYER_TYPE_OPTIONS = [
  'Private', 'Public', 'Self-Employed', 'Non-Profit', 'Government',
] as const

/* ──────────────── Job Relevance ─────────────────────────── */

export const JOB_RELEVANCE_OPTIONS = [
  'Highly Relevant', 'Relevant', 'Somewhat Relevant', 'Not Relevant',
] as const

/* ──────────────── Salary Range ──────────────────────────── */

export const SALARY_RANGE_OPTIONS = [
  'Under ₱20,000', '₱20,000 - ₱40,000', '₱40,000 - ₱60,000',
  '₱60,000 - ₱80,000', '₱80,000 - ₱100,000', 'Over ₱100,000',
] as const

/* ──────────────── Employment Length ─────────────────────── */

export const EMPLOYMENT_LENGTH_OPTIONS = [
  'Less than a month', '1-6 months', '6-12 months',
  '1-2 years', '2-5 years', 'More than 5 years',
] as const

/* ──────────────── Further Studies ───────────────────────── */

export const FURTHER_STUDIES_OPTIONS = [
  'Yes - Masters Degree', 'Yes - Doctorate', 'Yes - Professional Certification',
  'No', 'Planning to',
] as const

/* ──────────────── Experience Level ──────────────────────── */

export const EXPERIENCE_LEVEL_LABELS: Record<ExperienceLevel, string> = {
  entry: 'Entry Level',
  mid: 'Mid Level',
  senior: 'Senior',
  executive: 'Executive',
}

/* ──────────────── Employment Type ───────────────────────── */

export const EMPLOYMENT_TYPE_LABELS: Record<EmploymentType, string> = {
  full_time: 'Full Time',
  part_time: 'Part Time',
  contract: 'Contract',
  internship: 'Internship',
  freelance: 'Freelance',
}

/* ──────────────── Salary Labels (db key → display) ─────── */

export const SALARY_LABELS: Record<string, string> = {
  under_20k: 'Under ₱20,000',
  '20k_40k': '₱20,000 – ₱40,000',
  '40k_60k': '₱40,000 – ₱60,000',
  '60k_80k': '₱60,000 – ₱80,000',
  '80k_100k': '₱80,000 – ₱100,000',
  over_100k: 'Over ₱100,000',
}

/* ──────────────── Display ↔ DB converters ───────────────── */

type ConvertField = 'employment_status' | 'employer_type' | 'job_relevance' | 'salary_range' | 'employment_length'

const DB_MAPS: Record<ConvertField, Record<string, string>> = {
  employment_status: {
    'Employed': 'employed',
    'Unemployed': 'unemployed',
    'Self-Employed': 'self-employed',
    'Studying': 'studying',
    'Studying (Also Employed)': 'studying_employed',
  },
  employer_type: {
    'Private': 'private',
    'Public': 'public',
    'Self-Employed': 'self-employed',
    'Non-Profit': 'non-profit',
    'Government': 'government',
  },
  job_relevance: {
    'Highly Relevant': 'highly_relevant',
    'Relevant': 'relevant',
    'Somewhat Relevant': 'somewhat_relevant',
    'Not Relevant': 'not_relevant',
  },
  salary_range: {
    'Under ₱20,000': 'under_20k',
    '₱20,000 - ₱40,000': '20k_40k',
    '₱40,000 - ₱60,000': '40k_60k',
    '₱60,000 - ₱80,000': '60k_80k',
    '₱80,000 - ₱100,000': '80k_100k',
    'Over ₱100,000': 'over_100k',
  },
  employment_length: {
    'Less than a month': 'less_than_month',
    '1-6 months': '1_6_months',
    '6-12 months': '6_12_months',
    '1-2 years': '1_2_years',
    '2-5 years': '2_5_years',
    'More than 5 years': 'more_than_5_years',
  },
}

// Build reverse maps once
const DISPLAY_MAPS: Record<ConvertField, Record<string, string>> = {} as any
for (const field of Object.keys(DB_MAPS) as ConvertField[]) {
  const rev: Record<string, string> = {}
  for (const [display, db] of Object.entries(DB_MAPS[field])) {
    rev[db] = display
    // Also handle underscore variants (self_employed → Self-Employed)
    const alt = db.replace(/-/g, '_')
    if (alt !== db) rev[alt] = display
  }
  DISPLAY_MAPS[field] = rev
}

/** Convert a display value (e.g. "Self-Employed") to its DB value (e.g. "self-employed") */
export function toDbValue(displayValue: string, field: ConvertField): string | null {
  if (!displayValue) return null
  return DB_MAPS[field]?.[displayValue] ?? displayValue.toLowerCase().replace(/\s+/g, '_').replace(/\/+/g, '_')
}

/** Convert a DB value (e.g. "self-employed") to its display value (e.g. "Self-Employed") */
export function toDisplayValue(dbValue: string, field: ConvertField): string {
  if (!dbValue) return ''
  return DISPLAY_MAPS[field]?.[dbValue.toLowerCase()] ?? dbValue
}
